import type { Column, ColumnDef, ColumnPinningState } from "@tanstack/react-table";
import { dataTableConfig } from "@/config/data-table";
import type {
  ExtendedColumnFilter,
  FilterOperator,
  FilterVariant,
} from "@/types/data-table";

/** Shared column ids for admin DataTable layout (actions left + sticky cluster). */
export const DATA_TABLE_SELECT_COLUMN_ID = "select";
export const DATA_TABLE_ACTIONS_COLUMN_ID = "actions";

const ANCHOR_COLUMN_IDS = ["name", "student", "studentName", "username"] as const;

/**
 * Moves the actions column immediately after select (or to the front).
 * Applies sensible default widths for the sticky left cluster.
 */
export function prepareDataTableColumns<TData>(
  columns: ColumnDef<TData>[],
): ColumnDef<TData>[] {
  const actionsIndex = columns.findIndex(
    (column) => column.id === DATA_TABLE_ACTIONS_COLUMN_ID,
  );
  if (actionsIndex === -1) {
    return columns;
  }

  const withoutActions = columns.filter(
    (column) => column.id !== DATA_TABLE_ACTIONS_COLUMN_ID,
  );
  const actionsColumn = columns[actionsIndex];

  const selectIndex = withoutActions.findIndex(
    (column) => column.id === DATA_TABLE_SELECT_COLUMN_ID,
  );
  const insertAt = selectIndex >= 0 ? selectIndex + 1 : 0;

  const preparedActions: ColumnDef<TData> = {
    ...actionsColumn,
    size: actionsColumn.size ?? 56,
    minSize: actionsColumn.minSize ?? 52,
    maxSize: actionsColumn.maxSize ?? 72,
    enablePinning: actionsColumn.enablePinning ?? true,
    enableHiding: actionsColumn.enableHiding ?? false,
  };

  const prepared = [...withoutActions];
  prepared.splice(insertAt, 0, preparedActions);

  const actionsPosition = prepared.findIndex(
    (column) => column.id === DATA_TABLE_ACTIONS_COLUMN_ID,
  );
  const anchorIndex = prepared.findIndex(
    (column) =>
      column.id &&
      ANCHOR_COLUMN_IDS.includes(
        column.id as (typeof ANCHOR_COLUMN_IDS)[number],
      ),
  );

  if (
    anchorIndex !== -1 &&
    actionsPosition !== -1 &&
    anchorIndex !== actionsPosition + 1
  ) {
    const [anchorColumn] = prepared.splice(anchorIndex, 1);
    const insertAnchorAt =
      anchorIndex < actionsPosition ? actionsPosition : actionsPosition + 1;
    prepared.splice(insertAnchorAt, 0, anchorColumn);
  }

  return prepared.map((column) => {
    if (column.id === DATA_TABLE_SELECT_COLUMN_ID) {
      return {
        ...column,
        size: column.size ?? 44,
        minSize: column.minSize ?? 44,
        maxSize: column.maxSize ?? 44,
        enablePinning: column.enablePinning ?? true,
        enableHiding: column.enableHiding ?? false,
      };
    }

    if (
      column.id &&
      ANCHOR_COLUMN_IDS.includes(column.id as (typeof ANCHOR_COLUMN_IDS)[number])
    ) {
      return {
        ...column,
        size: column.size ?? 180,
        minSize: column.minSize ?? 140,
        enablePinning: column.enablePinning ?? true,
      };
    }

    return column;
  });
}

/** Default left-pinned cluster: select → actions → primary identifier column. */
export function buildDefaultColumnPinning(
  columnIds: string[],
): ColumnPinningState {
  const left: string[] = [];

  if (columnIds.includes(DATA_TABLE_SELECT_COLUMN_ID)) {
    left.push(DATA_TABLE_SELECT_COLUMN_ID);
  }
  if (columnIds.includes(DATA_TABLE_ACTIONS_COLUMN_ID)) {
    left.push(DATA_TABLE_ACTIONS_COLUMN_ID);
  }

  const anchorId = ANCHOR_COLUMN_IDS.find((id) => columnIds.includes(id));
  if (anchorId) {
    left.push(anchorId);
  }

  return { left, right: [] };
}

export function getCommonPinningStyles<TData>({
  column,
  withBorder = false,
}: {
  column: Column<TData>;
  withBorder?: boolean;
}): React.CSSProperties {
  const isPinned = column.getIsPinned();
  const isLastLeftPinnedColumn =
    isPinned === "left" && column.getIsLastColumn("left");
  const isFirstRightPinnedColumn =
    isPinned === "right" && column.getIsFirstColumn("right");

  return {
    boxShadow: withBorder
      ? isLastLeftPinnedColumn
        ? "-4px 0 4px -4px var(--border) inset"
        : isFirstRightPinnedColumn
          ? "4px 0 4px -4px var(--border) inset"
          : undefined
      : undefined,
    left: isPinned === "left" ? `${column.getStart("left")}px` : undefined,
    right: isPinned === "right" ? `${column.getAfter("right")}px` : undefined,
    position: isPinned ? "sticky" : "relative",
    ...(isPinned
      ? {
          backgroundColor: "var(--background)",
          opacity: 1,
        }
      : {}),
    width: column.getSize(),
    zIndex: isPinned ? 1 : undefined,
  };
}

export function getFilterOperators(filterVariant: FilterVariant) {
  const operatorMap: Record<
    FilterVariant,
    { label: string; value: FilterOperator }[]
  > = {
    text: dataTableConfig.textOperators,
    number: dataTableConfig.numericOperators,
    range: dataTableConfig.numericOperators,
    date: dataTableConfig.dateOperators,
    dateRange: dataTableConfig.dateOperators,
    boolean: dataTableConfig.booleanOperators,
    select: dataTableConfig.selectOperators,
    multiSelect: dataTableConfig.multiSelectOperators,
  };

  return operatorMap[filterVariant] ?? dataTableConfig.textOperators;
}

export function getDefaultFilterOperator(filterVariant: FilterVariant) {
  const operators = getFilterOperators(filterVariant);

  return operators[0]?.value ?? (filterVariant === "text" ? "iLike" : "eq");
}

export function getValidFilters<TData>(
  filters: ExtendedColumnFilter<TData>[],
): ExtendedColumnFilter<TData>[] {
  return filters.filter(
    (filter) =>
      filter.operator === "isEmpty" ||
      filter.operator === "isNotEmpty" ||
      (Array.isArray(filter.value)
        ? filter.value.length > 0
        : filter.value !== "" &&
          filter.value !== null &&
          filter.value !== undefined),
  );
}
